import { Loader2, X } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/components/shared";

// Team name/description limits. Single source of truth on the frontend.
// Mirrors backend/src/schemas/team.py (TeamCreate/TeamUpdate). Name is capped
// at 48 / description at 350 so they fit the team card/header UI without overflow.
export const TEAM_NAME_MAX = 48;
export const TEAM_DESCRIPTION_MAX = 350;

export interface TeamFormValues {
  name: string;
  description: string;
}

interface TeamFormModalProps {
  /** "create" or "edit" — drives the title, submit label, and placeholders. */
  mode: "create" | "edit";
  /** Initial field values (edit mode pre-fills; create mode typically blank). */
  initialValues?: TeamFormValues;
  /** True while the submit request is in flight. */
  busy?: boolean;
  /**
   * Called with the trimmed values when the form passes validation.
   * The parent performs the API call and closes the modal on success.
   */
  onSubmit: (values: TeamFormValues) => void;
  /** Called when the modal is dismissed (X / Cancel). */
  onClose: () => void;
}

/**
 * Shared create/edit team modal. Owns the name/description length limits,
 * live character counters, and client-side validation so those rules live in
 * exactly one place instead of being duplicated across the Teams and
 * TeamDetail pages.
 */
export function TeamFormModal({ mode, initialValues, busy = false, onSubmit, onClose }: Readonly<TeamFormModalProps>) {
  const { showToast } = useToast();
  const [name, setName] = useState(initialValues?.name ?? "");
  const [description, setDescription] = useState(initialValues?.description ?? "");

  const isCreate = mode === "create";
  const title = isCreate ? "Create Team" : "Edit Team";
  const submitIdleLabel = isCreate ? "Create Team" : "Save Changes";
  const submitBusyLabel = isCreate ? "Creating..." : "Saving...";

  const handleSubmit = () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      showToast("Please enter a team name", "warning");
      return;
    }
    if (trimmedName.length > TEAM_NAME_MAX) {
      showToast(`Team name must be ${TEAM_NAME_MAX} characters or fewer`, "warning");
      return;
    }
    if (description.trim().length > TEAM_DESCRIPTION_MAX) {
      showToast(`Description must be ${TEAM_DESCRIPTION_MAX} characters or fewer`, "warning");
      return;
    }
    onSubmit({ name: trimmedName, description: description.trim() });
  };

  return (
    <div className="fixed inset-0 bg-surface-overlay backdrop-blur-xs flex items-center justify-center z-50 p-4">
      <div className="bg-surface-primary border border-border-primary rounded-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-4 border-b border-border-primary">
          <h2 className="text-lg font-semibold text-content-primary">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="p-1 text-content-tertiary hover:text-content-primary transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label htmlFor="team-form-name" className="block text-sm font-medium text-content-secondary mb-1.5">
              Team Name <span className="text-red-400">*</span>
            </label>
            <input
              id="team-form-name"
              type="text"
              placeholder={isCreate ? "e.g., Engineering" : undefined}
              value={name}
              maxLength={TEAM_NAME_MAX}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
            />
            <p className="mt-1 text-xs text-content-quaternary text-right">
              {name.length}/{TEAM_NAME_MAX}
            </p>
          </div>
          <div>
            <label htmlFor="team-form-description" className="block text-sm font-medium text-content-secondary mb-1.5">
              Description{isCreate ? " (optional)" : ""}
            </label>
            <textarea
              id="team-form-description"
              rows={3}
              placeholder={isCreate ? "What does this team work on?" : undefined}
              value={description}
              maxLength={TEAM_DESCRIPTION_MAX}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary resize-none"
            />
            <p className="mt-1 text-xs text-content-quaternary text-right">
              {description.length}/{TEAM_DESCRIPTION_MAX}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t border-border-primary">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 text-content-secondary hover:text-content-primary transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={busy || !name.trim()}
            className="px-4 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                {submitBusyLabel}
              </>
            ) : (
              submitIdleLabel
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
